import { router } from "@inertiajs/react"
import { Plus } from "lucide-react"

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { t } from "@/lib/i18n"

/**
 * What the group is made of.
 *
 * Above the comparison, because it answers the question a group owner opening
 * this screen actually has — "what do I have" — and it is true even when nobody
 * has sold anything. The comparison below reads a period and is empty until two
 * branches have traded.
 *
 * The rows the server sends are the branches this person is staff at, never the
 * whole group. Listing a branch they hold no membership in would name a
 * restaurant they cannot open.
 *
 * A row navigates by switching the current branch, which is the same move the
 * navbar switcher makes — there is no other way to "open" a restaurant.
 */

export type Branch = {
  id: number
  name: string
  status: string
  statusLabel: string
  staffCount: number
  isCurrent: boolean
}

const tRoster = (key: string, options: Record<string, string | number> = {}) =>
  t(`ui.react.pages.staff.location.organization.show.roster.${key}`, options)

// The status tokens, never a raw hue. `disabled` is the honest mark for a
// suspended branch: it is not an error state the manager can act on from here.
const STATUS_DOT: Record<string, string> = {
  active: "bg-status-available",
  inactive: "bg-status-disabled",
  suspended: "bg-warning",
}

export function BranchRoster({
  branches,
  memberCount,
  organizationId,
  canAddBranch,
}: {
  branches: Branch[]
  memberCount: number
  organizationId: number
  canAddBranch: boolean
}) {
  return (
    <Card density="compact" data-testid="organization-roster-card">
      <CardHeader className="flex flex-row items-center justify-between gap-4 border-b">
        <CardTitle>{tRoster("title")}</CardTitle>

        {/* Carries the organization in the query string, so the branch is filed
            here rather than under whichever one the form would have guessed.
            Absent for anyone who does not own this organization: the service
            resolves the target through owned organizations, so the link would
            quietly file the branch somewhere else. */}
        {canAddBranch && (
          <Button
            className="h-11 md:h-9"
            data-testid="organization-add-branch"
            onClick={() => router.visit(`/locations/new?organization_id=${organizationId}`)}
          >
            <Plus className="size-4" />
            {tRoster("add_branch")}
          </Button>
        )}
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Two pluralized keys joined, not one string with two interpolations:
            "1 sucursales · 1 miembros" is what the single-string version prints,
            and a group of one branch is exactly the case a new customer sees. */}
        <p className="text-muted-foreground text-xs tabular-nums" data-testid="organization-member-count">
          {tRoster("branch_count", { count: branches.length })}
          {" · "}
          {tRoster("member_count", { count: memberCount })}
        </p>

        <ul className="divide-border divide-y rounded-md border" data-testid="organization-branch-list">
          {branches.map((branch) => (
            <li
              key={branch.id}
              className="flex flex-wrap items-center gap-3 px-4 py-3"
              data-testid={`organization-branch-${branch.id}`}
            >
              <span
                aria-hidden="true"
                className={`size-2 shrink-0 rounded-full ${STATUS_DOT[branch.status] ?? "bg-status-disabled"}`}
              />

              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium">
                  {branch.name}
                  {branch.isCurrent ? ` · ${tRoster("current")}` : ""}
                </span>
                <span className="text-muted-foreground block truncate text-xs tabular-nums">
                  {branch.statusLabel} · {tRoster("staff_count", { count: branch.staffCount })}
                </span>
              </span>

              {!branch.isCurrent && (
                <Button
                  variant="ghost"
                  className="h-11 md:h-9"
                  data-testid={`organization-branch-open-${branch.id}`}
                  onClick={() => router.post(`/locations/${branch.id}/use_location`)}
                >
                  {tRoster("open")}
                </Button>
              )}
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  )
}
